import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyDioceses, setCurrentDioceseId, type DioceseOption } from "@/lib/api";

const SS_KEY = "catho.economat.dioceseId";

interface DioceseScopeValue {
  dioceseId: string | null;
  setDioceseId: (id: string) => void;
  dioceses: DioceseOption[];
  isLoading: boolean;
}
const Ctx = createContext<DioceseScopeValue>({
  dioceseId: null, setDioceseId: () => {}, dioceses: [], isLoading: false,
});

export function useDioceseScope() { return useContext(Ctx); }

/**
 * Fournit le diocèse sélectionné (économat/SEDEC), persistant en sessionStorage
 * (URL propre, survit au F5 dans le même onglet). Le diocèse est envoyé au
 * backend via le header X-Diocese-Id (cf. apiFetch). Le backend revalide
 * TOUJOURS qu'il ∈ dioceseIds du JWT.
 */
export function DioceseScopeProvider({ children }: { children: ReactNode }) {
  const { data: dioceses = [], isLoading } = useQuery({
    queryKey: ["my-dioceses"],
    queryFn: getMyDioceses,
  });
  const [dioceseId, setDioceseIdState] = useState<string | null>(() => {
    try { return sessionStorage.getItem(SS_KEY); } catch { return null; }
  });

  // Au chargement des diocèses : si aucun sélectionné (ou sélection devenue
  // invalide), prendre le premier autorisé.
  useEffect(() => {
    if (dioceses.length === 0) return;
    const valid = dioceseId && dioceses.some((d) => d.id === dioceseId);
    if (!valid) {
      const first = dioceses[0].id;
      setDioceseIdState(first);
      try { sessionStorage.setItem(SS_KEY, first); } catch { /* ignore */ }
    }
  }, [dioceses, dioceseId]);

  // Garder apiFetch synchronisé avec le diocèse courant (montage + auto-select).
  useEffect(() => { setCurrentDioceseId(dioceseId); }, [dioceseId]);

  const setDioceseId = (id: string) => {
    setDioceseIdState(id);
    // SYNCHRONE : poser le diocèse courant AVANT que l'appelant n'invalide les
    // requêtes (DioceseSelector fait setDioceseId() puis invalidateQueries()).
    // Sans ça, le refetch immédiat partirait avec l'ancien diocèse car l'effet
    // ci-dessus ne s'exécute qu'après le commit React (race observée en E2E :
    // le dashboard restait sur le diocèse précédent après bascule).
    setCurrentDioceseId(id);
    try { sessionStorage.setItem(SS_KEY, id); } catch { /* ignore */ }
  };

  return (
    <Ctx.Provider value={{ dioceseId, setDioceseId, dioceses, isLoading }}>
      {children}
    </Ctx.Provider>
  );
}
